export class SoundManager {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this._footstepTimer = 0;
    this._initialized = false;

    // Cave reverb state
    this._caveMode = false;
    this._caveDepth = 0;        // 0 = surface, higher = deeper underground
    this._caveWetGain = null;    // wet mix level
    this._caveDryGain = null;    // dry mix level
    this._caveDelays = [];       // delay nodes for multi-tap reverb
    this._caveFeedbacks = [];    // feedback gain nodes
    this._caveFilter = null;     // low-pass on wet signal
  }

  _init() {
    if (this._initialized) return;
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();

    // --- Build routing graph with cave reverb ---
    //
    //  sources --> _caveDryGain --+--> masterGain --> destination
    //                             |
    //  sources --> _cavePreGain --+--> [delay taps + feedback] --> _caveFilter --> _caveWetGain --> masterGain
    //
    // When not in cave mode, _caveWetGain = 0 and _caveDryGain = 1.
    // Sources connect to this.masterSend (which feeds both dry and pre-reverb).

    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.3;
    this.masterGain.connect(this.ctx.destination);

    // Dry path
    this._caveDryGain = this.ctx.createGain();
    this._caveDryGain.gain.value = 1.0;
    this._caveDryGain.connect(this.masterGain);

    // Pre-reverb send
    this._cavePreGain = this.ctx.createGain();
    this._cavePreGain.gain.value = 1.0;

    // Multi-tap delay reverb (3 taps at different times for richer echo)
    const tapTimes = [0.12, 0.27, 0.43];
    const tapVolumes = [0.4, 0.3, 0.2];
    const feedbackAmount = 0.35;

    // Wet mix node (collects all delay taps)
    this._caveWetMix = this.ctx.createGain();
    this._caveWetMix.gain.value = 1.0;

    // Low-pass filter on wet signal (caves muffle high frequencies)
    this._caveFilter = this.ctx.createBiquadFilter();
    this._caveFilter.type = 'lowpass';
    this._caveFilter.frequency.value = 2500;
    this._caveFilter.Q.value = 0.7;

    // Wet output gain (0 when not in cave)
    this._caveWetGain = this.ctx.createGain();
    this._caveWetGain.gain.value = 0.0;

    // Wire: wetMix -> filter -> wetGain -> masterGain
    this._caveWetMix.connect(this._caveFilter);
    this._caveFilter.connect(this._caveWetGain);
    this._caveWetGain.connect(this.masterGain);

    // Create each delay tap with feedback
    this._caveDelays = [];
    this._caveFeedbacks = [];
    for (let i = 0; i < tapTimes.length; i++) {
      const delay = this.ctx.createDelay(1.0);
      delay.delayTime.value = tapTimes[i];

      const tapGain = this.ctx.createGain();
      tapGain.gain.value = tapVolumes[i];

      // Feedback loop: delay output -> feedback gain -> back into delay
      const feedback = this.ctx.createGain();
      feedback.gain.value = feedbackAmount;

      // Wire: preGain -> delay -> tapGain -> wetMix
      this._cavePreGain.connect(delay);
      delay.connect(tapGain);
      tapGain.connect(this._caveWetMix);

      // Feedback: delay -> feedback -> delay
      delay.connect(feedback);
      feedback.connect(delay);

      this._caveDelays.push(delay);
      this._caveFeedbacks.push(feedback);
    }

    // masterSend splits signal to dry and pre-reverb paths
    // All sound sources will connect to this.masterSend instead of this.masterGain
    this.masterSend = this.ctx.createGain();
    this.masterSend.gain.value = 1.0;
    this.masterSend.connect(this._caveDryGain);
    this.masterSend.connect(this._cavePreGain);

    this._initialized = true;
  }

  /**
   * Set cave reverb mode based on player position.
   * @param {boolean} isUnderground - true if player is underground (y < 40 with solid block above)
   * @param {number} depth - How far below the surface (0 = at surface threshold, higher = deeper).
   *                         Typically computed as (surfaceThreshold - playerY), e.g. (40 - playerY).
   */
  setCaveMode(isUnderground, depth = 0) {
    this._init();
    this._caveMode = isUnderground;
    this._caveDepth = Math.max(0, depth);

    if (!isUnderground) {
      // No reverb on surface — smoothly fade out
      this._caveWetGain.gain.linearRampToValueAtTime(0.0, this.ctx.currentTime + 0.5);
      this._caveDryGain.gain.linearRampToValueAtTime(1.0, this.ctx.currentTime + 0.5);
      this._caveFilter.frequency.linearRampToValueAtTime(2500, this.ctx.currentTime + 0.5);
      for (const fb of this._caveFeedbacks) {
        fb.gain.linearRampToValueAtTime(0.35, this.ctx.currentTime + 0.5);
      }
      return;
    }

    // Depth factor: 0 at surface, 1 at ~40 blocks deep, clamped
    const depthFactor = Math.min(1.0, this._caveDepth / 40);

    // Wet mix: 0.15 at shallow caves, up to 0.55 deep underground
    const wetLevel = 0.15 + depthFactor * 0.4;
    this._caveWetGain.gain.linearRampToValueAtTime(wetLevel, this.ctx.currentTime + 0.3);

    // Dry slightly reduced when very deep (sounds more enclosed)
    const dryLevel = 1.0 - depthFactor * 0.15;
    this._caveDryGain.gain.linearRampToValueAtTime(dryLevel, this.ctx.currentTime + 0.3);

    // Deeper = more muffled wet signal (lower cutoff)
    const filterFreq = 2500 - depthFactor * 1500; // 2500 -> 1000
    this._caveFilter.frequency.linearRampToValueAtTime(filterFreq, this.ctx.currentTime + 0.3);

    // Deeper = more feedback (longer tail)
    const feedbackVal = 0.35 + depthFactor * 0.25; // 0.35 -> 0.60
    for (const fb of this._caveFeedbacks) {
      fb.gain.linearRampToValueAtTime(feedbackVal, this.ctx.currentTime + 0.3);
    }

    // Scale delay times slightly with depth for larger-sounding space
    const baseDelays = [0.12, 0.27, 0.43];
    for (let i = 0; i < this._caveDelays.length; i++) {
      const scaledTime = baseDelays[i] * (1.0 + depthFactor * 0.6); // up to 60% longer delays
      this._caveDelays[i].delayTime.linearRampToValueAtTime(scaledTime, this.ctx.currentTime + 0.3);
    }
  }

  resume() {
    this._init();
    if (this.ctx.state === 'suspended') this.ctx.resume();
  }


  // ============================================================
  //  BLOCK MATERIAL CLASSIFICATION
  // ============================================================

  /**
   * Get the material category of a block for sound purposes.
   * @param {number} blockId
   * @returns {'stone'|'wood'|'dirt'|'glass'|'metal'|'wool'|'leaves'|'sand'|'default'}
   */
  static getBlockMaterial(blockId) {
    // Stone / Cobble / Brick / Ore / Obsidian / Nether Bricks / End Stone
    if ([3, 13, 25, 33, 34, 35, 36, 37, 55, 56, 59, 60, 83, 88, 91,
         92, 94, 95, 96].includes(blockId) ||
        (blockId >= 18 && blockId <= 24) || blockId === 74 || blockId === 75) return 'stone';
    // Wood / Planks / Log / Crafting Table / Bookshelf / Fence / Ladder / Doors / Slabs / Stairs
    if ([5, 12, 27, 32, 38, 39, 40, 48, 64, 66, 84, 86, 87, 16, 17, 82, 85, 89, 90].includes(blockId)) return 'wood';
    // Dirt / Grass / Farmland / Clay / Mushrooms / Flowers
    if ([1, 2, 9, 41, 42, 43, 44, 45, 47, 62, 63].includes(blockId)) return 'dirt';
    // Sand / Gravel / Soul Sand
    if ([4, 10, 46, 58].includes(blockId)) return 'sand';
    // Glass
    if (blockId === 14 || blockId === 51) return 'glass';
    // Metal blocks (Iron, Gold, Diamond)
    if (blockId === 34 || blockId === 35 || blockId === 36 || blockId === 70 || blockId === 71 || blockId === 99) return 'metal';
    // Wool
    if (blockId === 54) return 'wool';
    // Leaves / Plants / Vines
    if ([6, 11, 15, 49, 65, 67, 68, 69, 97].includes(blockId)) return 'leaves';
    // Snow
    if (blockId === 50) return 'sand'; // snow sounds similar to sand (soft crunch)
    return 'default';
  }

  // ============================================================
  //  BLOCK-SPECIFIC SOUNDS
  // ============================================================

  /**
   * Play a sound appropriate for the block material.
   * @param {number} blockId - The block being broken or placed
   * @param {'break'|'place'} action
   */
  playBlockSound(blockId, action) {
    this._init();
    const material = SoundManager.getBlockMaterial(blockId);
    if (action === 'break') {
      this._playBlockBreakSound(material);
    } else {
      this._playBlockPlaceSound(material);
    }
  }

  _playBlockBreakSound(material) {
    const t = this.ctx.currentTime;
    switch (material) {
      case 'stone': {
        // Sharp crack: high-freq noise burst + low thud
        const dur = 0.12;
        const buf = this._noiseBuffer(dur);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = 1800 + Math.random() * 600;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.3, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
        src.connect(filter).connect(gain).connect(this.masterSend);
        src.start(t);
        // Low thud
        const osc = this.ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(120, t);
        osc.frequency.exponentialRampToValueAtTime(40, t + 0.08);
        const tg = this.ctx.createGain();
        tg.gain.setValueAtTime(0.2, t);
        tg.gain.exponentialRampToValueAtTime(0.01, t + 0.1);
        osc.connect(tg).connect(this.masterSend);
        osc.start(t);
        osc.stop(t + 0.1);
        break;
      }
      case 'wood': {
        // Woody creak: mid-freq with resonance
        const dur = 0.18;
        const osc = this.ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(300 + Math.random() * 100, t);
        osc.frequency.exponentialRampToValueAtTime(120, t + dur);
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 400;
        filter.Q.value = 4;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.25, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
        osc.connect(filter).connect(gain).connect(this.masterSend);
        osc.start(t);
        osc.stop(t + dur);
        // Wood crack noise
        const nbuf = this._noiseBuffer(0.06);
        const ns = this.ctx.createBufferSource();
        ns.buffer = nbuf;
        const nf = this.ctx.createBiquadFilter();
        nf.type = 'bandpass';
        nf.frequency.value = 800;
        nf.Q.value = 2;
        const ng = this.ctx.createGain();
        ng.gain.setValueAtTime(0.12, t);
        ng.gain.exponentialRampToValueAtTime(0.01, t + 0.06);
        ns.connect(nf).connect(ng).connect(this.masterSend);
        ns.start(t);
        break;
      }
      case 'dirt': {
        // Soft crunch: low noise, muffled
        const dur = 0.12;
        const buf = this._noiseBuffer(dur);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 500 + Math.random() * 200;
        filter.Q.value = 0.8;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.2, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
        src.connect(filter).connect(gain).connect(this.masterSend);
        src.start(t);
        break;
      }
      case 'sand': {
        // Soft sandy crunch: very low noise, longer
        const dur = 0.15;
        const buf = this._noiseBuffer(dur);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 400 + Math.random() * 150;
        filter.Q.value = 0.5;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.18, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
        src.connect(filter).connect(gain).connect(this.masterSend);
        src.start(t);
        break;
      }
      case 'glass': {
        // High-pitched shatter: sine sweep down + noise burst
        const dur = 0.25;
        const osc = this.ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(3000 + Math.random() * 1000, t);
        osc.frequency.exponentialRampToValueAtTime(400, t + dur);
        const og = this.ctx.createGain();
        og.gain.setValueAtTime(0.15, t);
        og.gain.exponentialRampToValueAtTime(0.01, t + dur);
        osc.connect(og).connect(this.masterSend);
        osc.start(t);
        osc.stop(t + dur);
        // Shimmer noise
        const buf = this._noiseBuffer(dur);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = 4000;
        const ng = this.ctx.createGain();
        ng.gain.setValueAtTime(0.2, t);
        ng.gain.exponentialRampToValueAtTime(0.01, t + dur);
        src.connect(filter).connect(ng).connect(this.masterSend);
        src.start(t);
        break;
      }
      case 'metal': {
        // Metallic ring: sine with harmonics, longer decay
        const dur = 0.4;
        const baseFreq = 600 + Math.random() * 200;
        [1, 2.2, 3.5].forEach((harmonic, i) => {
          const osc = this.ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = baseFreq * harmonic;
          const g = this.ctx.createGain();
          const vol = 0.12 / (i + 1);
          g.gain.setValueAtTime(vol, t);
          g.gain.exponentialRampToValueAtTime(0.001, t + dur);
          osc.connect(g).connect(this.masterSend);
          osc.start(t);
          osc.stop(t + dur);
        });
        break;
      }
      case 'wool': {
        // Soft puff: very quiet, low noise
        const dur = 0.1;
        const buf = this._noiseBuffer(dur);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 300;
        filter.Q.value = 0.5;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.08, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
        src.connect(filter).connect(gain).connect(this.masterSend);
        src.start(t);
        break;
      }
      case 'leaves': {
        // Rustle: filtered noise, quick
        const dur = 0.1;
        const buf = this._noiseBuffer(dur);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 2000 + Math.random() * 1000;
        filter.Q.value = 0.8;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.12, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
        src.connect(filter).connect(gain).connect(this.masterSend);
        src.start(t);
        break;
      }
      default: {
        // Fallback: original generic break sound
        const dur = 0.15;
        const buf = this._noiseBuffer(dur);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.3, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 600 + Math.random() * 400;
        filter.Q.value = 1.0;
        src.connect(filter).connect(gain).connect(this.masterSend);
        src.start(t);
        break;
      }
    }
  }

  _playBlockPlaceSound(material) {
    const t = this.ctx.currentTime;
    switch (material) {
      case 'stone': {
        // Heavy thunk
        const osc = this.ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(200, t);
        osc.frequency.exponentialRampToValueAtTime(80, t + 0.1);
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.25, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + 0.12);
        osc.connect(gain).connect(this.masterSend);
        osc.start(t);
        osc.stop(t + 0.12);
        // Grit noise
        const buf = this._noiseBuffer(0.05);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const nf = this.ctx.createBiquadFilter();
        nf.type = 'highpass';
        nf.frequency.value = 1500;
        const ng = this.ctx.createGain();
        ng.gain.setValueAtTime(0.08, t);
        ng.gain.exponentialRampToValueAtTime(0.01, t + 0.05);
        src.connect(nf).connect(ng).connect(this.masterSend);
        src.start(t);
        break;
      }
      case 'wood': {
        // Woody knock
        const osc = this.ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(250, t);
        osc.frequency.exponentialRampToValueAtTime(100, t + 0.1);
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 350;
        filter.Q.value = 3;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.22, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + 0.12);
        osc.connect(filter).connect(gain).connect(this.masterSend);
        osc.start(t);
        osc.stop(t + 0.12);
        break;
      }
      case 'dirt': {
        // Soft thump
        const osc = this.ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(140, t);
        osc.frequency.exponentialRampToValueAtTime(50, t + 0.1);
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.18, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + 0.12);
        osc.connect(gain).connect(this.masterSend);
        osc.start(t);
        osc.stop(t + 0.12);
        break;
      }
      case 'sand': {
        // Soft poof
        const dur = 0.12;
        const buf = this._noiseBuffer(dur);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 350;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.15, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
        src.connect(filter).connect(gain).connect(this.masterSend);
        src.start(t);
        break;
      }
      case 'glass': {
        // Delicate clink
        const osc = this.ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(2000, t);
        osc.frequency.exponentialRampToValueAtTime(1200, t + 0.15);
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.12, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + 0.2);
        osc.connect(gain).connect(this.masterSend);
        osc.start(t);
        osc.stop(t + 0.2);
        break;
      }
      case 'metal': {
        // Metallic clang
        const dur = 0.3;
        const baseFreq = 500 + Math.random() * 150;
        [1, 1.8, 2.9].forEach((h, i) => {
          const osc = this.ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = baseFreq * h;
          const g = this.ctx.createGain();
          const vol = 0.1 / (i + 1);
          g.gain.setValueAtTime(vol, t);
          g.gain.exponentialRampToValueAtTime(0.001, t + dur);
          osc.connect(g).connect(this.masterSend);
          osc.start(t);
          osc.stop(t + dur);
        });
        break;
      }
      case 'wool': {
        // Very soft thud
        const osc = this.ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(100, t);
        osc.frequency.exponentialRampToValueAtTime(40, t + 0.08);
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.06, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + 0.1);
        osc.connect(gain).connect(this.masterSend);
        osc.start(t);
        osc.stop(t + 0.1);
        break;
      }
      case 'leaves': {
        // Light rustle
        const dur = 0.08;
        const buf = this._noiseBuffer(dur);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 1800 + Math.random() * 800;
        filter.Q.value = 0.6;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.08, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
        src.connect(filter).connect(gain).connect(this.masterSend);
        src.start(t);
        break;
      }
      default: {
        // Fallback: original generic place sound
        const osc = this.ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(180, t);
        osc.frequency.exponentialRampToValueAtTime(60, t + 0.1);
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.25, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + 0.12);
        osc.connect(gain).connect(this.masterSend);
        osc.start(t);
        osc.stop(t + 0.12);
        break;
      }
    }
  }

  /** @deprecated Use playBlockSound(blockId, 'break') for material-specific sounds */
  playBreak(blockId) {
    this._init();
    if (blockId !== undefined) {
      this._playBlockBreakSound(SoundManager.getBlockMaterial(blockId));
    } else {
      this._playBlockBreakSound('default');
    }
  }

  /** @deprecated Use playBlockSound(blockId, 'place') for material-specific sounds */
  playPlace(blockId) {
    this._init();
    if (blockId !== undefined) {
      this._playBlockPlaceSound(SoundManager.getBlockMaterial(blockId));
    } else {
      this._playBlockPlaceSound('default');
    }
  }

  playJump() {
    this._init();
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(220, t);
    osc.frequency.exponentialRampToValueAtTime(440, t + 0.1);
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.15);
    osc.connect(gain).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + 0.15);
  }

  playSplash() {
    this._init();
    const t = this.ctx.currentTime;
    const dur = 0.35;
    const buf = this._noiseBuffer(dur);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.4, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 400;
    src.connect(filter).connect(gain).connect(this.masterSend);
    src.start(t);
  }

  playFootstep(surfaceType) {
    this._init();
    const t = this.ctx.currentTime;
    // Different sounds for different surface materials
    const params = this._getFootstepParams(surfaceType || 'dirt');
    const osc = this.ctx.createOscillator();
    osc.type = params.wave;
    osc.frequency.value = params.freq + Math.random() * params.freqRange;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(params.vol, t);
    gain.gain.exponentialRampToValueAtTime(0.005, t + params.dur);
    // Add noise for crunchy sounds (stone, gravel, sand)
    if (params.noise) {
      const bufferSize = this.ctx.sampleRate * params.dur;
      const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.3;
      const noise = this.ctx.createBufferSource();
      noise.buffer = buffer;
      const nGain = this.ctx.createGain();
      nGain.gain.setValueAtTime(params.noiseVol, t);
      nGain.gain.exponentialRampToValueAtTime(0.001, t + params.dur);
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = params.noiseFreq;
      filter.Q.value = 1;
      noise.connect(filter).connect(nGain).connect(this.masterSend);
      noise.start(t);
      noise.stop(t + params.dur);
    }
    osc.connect(gain).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + params.dur);
  }

  _getFootstepParams(surfaceType) {
    switch (surfaceType) {
      case 'grass':
        return { wave: 'triangle', freq: 60, freqRange: 40, vol: 0.05, dur: 0.08, noise: true, noiseVol: 0.03, noiseFreq: 2000 };
      case 'stone':
        return { wave: 'square', freq: 100, freqRange: 80, vol: 0.04, dur: 0.05, noise: true, noiseVol: 0.04, noiseFreq: 4000 };
      case 'sand':
        return { wave: 'sine', freq: 40, freqRange: 30, vol: 0.04, dur: 0.1, noise: true, noiseVol: 0.05, noiseFreq: 1500 };
      case 'wood':
        return { wave: 'triangle', freq: 120, freqRange: 60, vol: 0.06, dur: 0.06, noise: false, noiseVol: 0, noiseFreq: 0 };
      case 'snow':
        return { wave: 'sine', freq: 30, freqRange: 20, vol: 0.03, dur: 0.12, noise: true, noiseVol: 0.06, noiseFreq: 800 };
      case 'gravel':
        return { wave: 'sawtooth', freq: 50, freqRange: 50, vol: 0.04, dur: 0.07, noise: true, noiseVol: 0.05, noiseFreq: 3000 };
      default: // dirt
        return { wave: 'triangle', freq: 80, freqRange: 60, vol: 0.06, dur: 0.06, noise: false, noiseVol: 0, noiseFreq: 0 };
    }
  }

  /**
   * Get surface type for footstep sounds based on block ID.
   */
  static getSurfaceType(blockId) {
    // grass-like: grass, tall grass, farmland
    if (blockId === 1 || blockId === 9 || blockId === 43 || blockId === 62 || blockId === 63) return 'grass';
    // stone-like: stone, cobblestone, ores, bricks, obsidian
    if (blockId === 3 || blockId === 13 || blockId === 25 || blockId === 33 || blockId === 8 ||
        (blockId >= 18 && blockId <= 24) || blockId === 34 || blockId === 35 || blockId === 36) return 'stone';
    // sand-like: sand, sandstone, soul sand
    if (blockId === 4 || blockId === 10 || blockId === 58) return 'sand';
    // wood-like: planks, logs, crafting table
    if (blockId === 5 || blockId === 12 || blockId === 27 || blockId === 32 || blockId === 38 ||
        blockId === 48 || blockId === 64 || blockId === 66) return 'wood';
    // snow
    if (blockId === 50 || blockId === 51) return 'snow';
    // gravel
    if (blockId === 46 || blockId === 47) return 'gravel';
    return 'dirt';
  }

  updateFootsteps(dt, isWalking, isOnGround, surfaceBlockId) {
    if (isWalking && isOnGround) {
      this._footstepTimer -= dt;
      if (this._footstepTimer <= 0) {
        const surfaceType = SoundManager.getSurfaceType(surfaceBlockId || 2);
        this.playFootstep(surfaceType);
        this._footstepTimer = 0.35;
      }
    } else {
      this._footstepTimer = 0;
    }
  }

  // ============================================================
  //  MOB SOUNDS (3D positional, volume based on distance)
  // ============================================================

  // Play a mob sound with distance-based volume
  _playMobSound(playerPos, mobPos, maxDist, fn) {
    this._init();
    const dx = playerPos.x - mobPos.x;
    const dy = playerPos.y - mobPos.y;
    const dz = playerPos.z - mobPos.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist > maxDist) return;

    // Volume falls off with distance
    const vol = Math.max(0, 1 - dist / maxDist);
    // Stereo panning based on relative position (simple L/R)
    const pan = Math.max(-1, Math.min(1, (mobPos.x - playerPos.x) / Math.max(dist, 1)));

    const gainNode = this.ctx.createGain();
    gainNode.gain.value = vol * 0.35;

    const panNode = this.ctx.createStereoPanner();
    panNode.pan.value = pan;

    gainNode.connect(panNode).connect(this.masterSend);
    fn(this.ctx, gainNode);
  }

  // --- PIG: "oink" - nasal short tone with pitch bend ---
  playPigSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 20, (ctx, output) => {
      const t = ctx.currentTime;
      const baseFreq = 200 + Math.random() * 80;

      // Main nasal tone
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(baseFreq, t);
      osc.frequency.linearRampToValueAtTime(baseFreq * 1.3, t + 0.05);
      osc.frequency.linearRampToValueAtTime(baseFreq * 0.8, t + 0.15);

      // Nasal filter
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 500 + Math.random() * 200;
      filter.Q.value = 5;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.4, t);
      gain.gain.linearRampToValueAtTime(0.6, t + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.18);

      osc.connect(filter).connect(gain).connect(output);
      osc.start(t);
      osc.stop(t + 0.2);

      // Second "oink" (optional double grunt)
      if (Math.random() < 0.4) {
        const osc2 = ctx.createOscillator();
        osc2.type = 'sawtooth';
        osc2.frequency.setValueAtTime(baseFreq * 0.9, t + 0.22);
        osc2.frequency.linearRampToValueAtTime(baseFreq * 1.1, t + 0.27);
        osc2.frequency.linearRampToValueAtTime(baseFreq * 0.7, t + 0.35);
        const gain2 = ctx.createGain();
        gain2.gain.setValueAtTime(0.01, t + 0.22);
        gain2.gain.linearRampToValueAtTime(0.35, t + 0.25);
        gain2.gain.exponentialRampToValueAtTime(0.01, t + 0.38);
        osc2.connect(filter).connect(gain2).connect(output);
        osc2.start(t + 0.22);
        osc2.stop(t + 0.4);
      }
    });
  }

  // --- SHEEP: "baa" - vowel-like with formant filtering ---
  playSheepSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 22, (ctx, output) => {
      const t = ctx.currentTime;
      const baseFreq = 280 + Math.random() * 60;
      const dur = 0.35 + Math.random() * 0.15;

      // Vocal cord oscillator
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(baseFreq, t);
      osc.frequency.linearRampToValueAtTime(baseFreq * 1.15, t + dur * 0.3);
      osc.frequency.linearRampToValueAtTime(baseFreq * 0.85, t + dur);

      // Formant 1 (the "aa" vowel ~700Hz)
      const f1 = ctx.createBiquadFilter();
      f1.type = 'bandpass';
      f1.frequency.value = 700;
      f1.Q.value = 6;

      // Formant 2 (~1200Hz)
      const f2 = ctx.createBiquadFilter();
      f2.type = 'bandpass';
      f2.frequency.value = 1200;
      f2.Q.value = 4;

      const gain1 = ctx.createGain();
      gain1.gain.setValueAtTime(0.01, t);
      gain1.gain.linearRampToValueAtTime(0.5, t + 0.05);
      gain1.gain.setValueAtTime(0.5, t + dur * 0.6);
      gain1.gain.exponentialRampToValueAtTime(0.01, t + dur);

      const gain2 = ctx.createGain();
      gain2.gain.value = 0.25;

      // Mix both formants
      osc.connect(f1).connect(gain1).connect(output);
      osc.connect(f2).connect(gain2).connect(output);
      osc.start(t);
      osc.stop(t + dur + 0.05);
    });
  }

  // --- COW: "moo" - low pitch, long, with formant shift ---
  playCowSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 25, (ctx, output) => {
      const t = ctx.currentTime;
      const baseFreq = 120 + Math.random() * 30;
      const dur = 0.6 + Math.random() * 0.3;

      // Vocal cord
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(baseFreq, t);
      osc.frequency.linearRampToValueAtTime(baseFreq * 1.2, t + dur * 0.4);
      osc.frequency.linearRampToValueAtTime(baseFreq * 0.9, t + dur);

      // "oo" formant (~400Hz)
      const f1 = ctx.createBiquadFilter();
      f1.type = 'bandpass';
      f1.frequency.setValueAtTime(300, t);
      f1.frequency.linearRampToValueAtTime(500, t + dur * 0.3);
      f1.frequency.linearRampToValueAtTime(350, t + dur);
      f1.Q.value = 5;

      // Sub-bass resonance
      const f2 = ctx.createBiquadFilter();
      f2.type = 'lowpass';
      f2.frequency.value = 500;
      f2.Q.value = 2;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.01, t);
      gain.gain.linearRampToValueAtTime(0.6, t + 0.08);
      gain.gain.setValueAtTime(0.55, t + dur * 0.5);
      gain.gain.exponentialRampToValueAtTime(0.01, t + dur);

      osc.connect(f1).connect(gain).connect(output);
      osc.connect(f2).connect(gain);
      osc.start(t);
      osc.stop(t + dur + 0.05);
    });
  }

  // --- CHICKEN: "bok bok" - short staccato clicks ---
  playChickenSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 18, (ctx, output) => {
      const t = ctx.currentTime;
      const numClucks = 2 + Math.floor(Math.random() * 3); // 2-4 clucks

      for (let i = 0; i < numClucks; i++) {
        const offset = i * (0.12 + Math.random() * 0.06);
        const freq = 800 + Math.random() * 400;

        const osc = ctx.createOscillator();
        osc.type = 'square';
        osc.frequency.setValueAtTime(freq, t + offset);
        osc.frequency.exponentialRampToValueAtTime(freq * 0.5, t + offset + 0.06);

        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 1200;
        filter.Q.value = 3;

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.01, t + offset);
        gain.gain.linearRampToValueAtTime(0.4, t + offset + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.01, t + offset + 0.07);

        osc.connect(filter).connect(gain).connect(output);
        osc.start(t + offset);
        osc.stop(t + offset + 0.08);
      }
    });
  }

  // --- VILLAGER: "hmm/hrm" - nasal humming with consonant ---
  playVillagerSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 15, (ctx, output) => {
      const t = ctx.currentTime;
      const baseFreq = 100 + Math.random() * 50;
      const dur = 0.25 + Math.random() * 0.2;
      const isQuestion = Math.random() < 0.4; // rising pitch = question

      // Main nasal hum
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';

      if (isQuestion) {
        // "Hmm?" - rising pitch
        osc.frequency.setValueAtTime(baseFreq, t);
        osc.frequency.linearRampToValueAtTime(baseFreq * 1.5, t + dur * 0.8);
        osc.frequency.setValueAtTime(baseFreq * 1.5, t + dur);
      } else {
        // "Hrm" - falling pitch
        osc.frequency.setValueAtTime(baseFreq * 1.2, t);
        osc.frequency.linearRampToValueAtTime(baseFreq * 0.8, t + dur);
      }

      // Very nasal filter (tight bandpass)
      const nasal = ctx.createBiquadFilter();
      nasal.type = 'bandpass';
      nasal.frequency.value = 350 + Math.random() * 150;
      nasal.Q.value = 10;

      // Secondary resonance for "nose" quality
      const nasal2 = ctx.createBiquadFilter();
      nasal2.type = 'bandpass';
      nasal2.frequency.value = 800;
      nasal2.Q.value = 6;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.01, t);
      gain.gain.linearRampToValueAtTime(0.5, t + 0.03);
      gain.gain.setValueAtTime(0.45, t + dur * 0.7);
      gain.gain.exponentialRampToValueAtTime(0.01, t + dur);

      const gain2 = ctx.createGain();
      gain2.gain.value = 0.2;

      osc.connect(nasal).connect(gain).connect(output);
      osc.connect(nasal2).connect(gain2).connect(output);
      osc.start(t);
      osc.stop(t + dur + 0.02);

      // Sometimes add a second syllable ("hmm hmm")
      if (Math.random() < 0.35) {
        const delay = dur + 0.08;
        const dur2 = 0.15 + Math.random() * 0.1;
        const osc2 = ctx.createOscillator();
        osc2.type = 'sawtooth';
        osc2.frequency.setValueAtTime(baseFreq * 0.9, t + delay);
        osc2.frequency.linearRampToValueAtTime(baseFreq * 0.7, t + delay + dur2);

        const g2 = ctx.createGain();
        g2.gain.setValueAtTime(0.01, t + delay);
        g2.gain.linearRampToValueAtTime(0.3, t + delay + 0.02);
        g2.gain.exponentialRampToValueAtTime(0.01, t + delay + dur2);

        osc2.connect(nasal).connect(g2).connect(output);
        osc2.start(t + delay);
        osc2.stop(t + delay + dur2 + 0.02);
      }
    });
  }

  // --- HEROBRINE: eerie low drone / creepy ambient ---
  playHerobrineAmbient(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 60, (ctx, output) => {
      const t = ctx.currentTime;
      const dur = 2.5;

      // Deep sub-bass drone
      const osc1 = ctx.createOscillator();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(40, t);
      osc1.frequency.linearRampToValueAtTime(38, t + dur);

      // Dissonant overtone
      const osc2 = ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(61, t); // Slightly detuned from harmonic
      osc2.frequency.linearRampToValueAtTime(58, t + dur);

      // High eerie whistle
      const osc3 = ctx.createOscillator();
      osc3.type = 'sine';
      osc3.frequency.setValueAtTime(800 + Math.random() * 200, t);
      osc3.frequency.linearRampToValueAtTime(600, t + dur);

      const gain1 = ctx.createGain();
      gain1.gain.setValueAtTime(0.01, t);
      gain1.gain.linearRampToValueAtTime(0.5, t + 0.5);
      gain1.gain.linearRampToValueAtTime(0.4, t + dur * 0.7);
      gain1.gain.exponentialRampToValueAtTime(0.01, t + dur);

      const gain2 = ctx.createGain();
      gain2.gain.setValueAtTime(0.01, t);
      gain2.gain.linearRampToValueAtTime(0.25, t + 0.8);
      gain2.gain.exponentialRampToValueAtTime(0.01, t + dur);

      const gain3 = ctx.createGain();
      gain3.gain.setValueAtTime(0.01, t);
      gain3.gain.linearRampToValueAtTime(0.06, t + 1.0);
      gain3.gain.exponentialRampToValueAtTime(0.01, t + dur);

      // Tremolo on the whistle
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 4;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.03;
      lfo.connect(lfoGain).connect(gain3.gain);
      lfo.start(t);
      lfo.stop(t + dur);

      osc1.connect(gain1).connect(output);
      osc2.connect(gain2).connect(output);
      osc3.connect(gain3).connect(output);
      osc1.start(t);
      osc2.start(t);
      osc3.start(t);
      osc1.stop(t + dur);
      osc2.stop(t + dur);
      osc3.stop(t + dur);
    });
  }

  // --- HEROBRINE vanish: sudden reverse reverb / whoosh ---
  playHerobrineVanish(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 40, (ctx, output) => {
      const t = ctx.currentTime;
      const dur = 0.6;

      // Reverse crash/whoosh
      const buf = this._noiseBuffer(dur);
      const src = ctx.createBufferSource();
      src.buffer = buf;

      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(2000, t);
      filter.frequency.exponentialRampToValueAtTime(100, t + dur);
      filter.Q.value = 2;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.5, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + dur);

      src.connect(filter).connect(gain).connect(output);
      src.start(t);

      // Low boom
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(80, t);
      osc.frequency.exponentialRampToValueAtTime(20, t + dur);
      const boomGain = ctx.createGain();
      boomGain.gain.setValueAtTime(0.4, t);
      boomGain.gain.exponentialRampToValueAtTime(0.01, t + dur);
      osc.connect(boomGain).connect(output);
      osc.start(t);
      osc.stop(t + dur);
    });
  }

  // --- ZOMBIE: deep groan ---
  playZombieSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 25, (ctx, output) => {
      const t = ctx.currentTime;
      const dur = 0.5 + Math.random() * 0.3;
      const baseFreq = 70 + Math.random() * 20;
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(baseFreq, t);
      osc.frequency.linearRampToValueAtTime(baseFreq * 0.7, t + dur);
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 200;
      filter.Q.value = 4;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.01, t);
      gain.gain.linearRampToValueAtTime(0.5, t + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
      osc.connect(filter).connect(gain).connect(output);
      osc.start(t);
      osc.stop(t + dur);
    });
  }

  // --- SKELETON: bone rattle ---
  playSkeletonSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 25, (ctx, output) => {
      const t = ctx.currentTime;
      for (let i = 0; i < 4; i++) {
        const off = i * 0.06;
        const buf = this._noiseBuffer(0.03);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        const filter = ctx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = 2000 + Math.random() * 2000;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.4, t + off);
        gain.gain.exponentialRampToValueAtTime(0.01, t + off + 0.04);
        src.connect(filter).connect(gain).connect(output);
        src.start(t + off);
      }
    });
  }

  // --- CREEPER: hiss ---
  playCreeperSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 20, (ctx, output) => {
      const t = ctx.currentTime;
      const dur = 0.8;
      const buf = this._noiseBuffer(dur);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.setValueAtTime(3000, t);
      filter.frequency.linearRampToValueAtTime(6000, t + dur);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.01, t);
      gain.gain.linearRampToValueAtTime(0.4, t + dur * 0.8);
      gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
      src.connect(filter).connect(gain).connect(output);
      src.start(t);
    });
  }

  // --- SPIDER: clicks ---
  playSpiderSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 20, (ctx, output) => {
      const t = ctx.currentTime;
      for (let i = 0; i < 3; i++) {
        const off = i * 0.08;
        const osc = ctx.createOscillator();
        osc.type = 'square';
        osc.frequency.value = 400 + Math.random() * 200;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.3, t + off);
        gain.gain.exponentialRampToValueAtTime(0.01, t + off + 0.04);
        osc.connect(gain).connect(output);
        osc.start(t + off);
        osc.stop(t + off + 0.05);
      }
    });
  }

  // --- ENDERMAN: eerie warped sound / static ---
  playEndermanSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 30, (ctx, output) => {
      const t = ctx.currentTime;
      const dur = 0.4 + Math.random() * 0.3;

      // Distorted static/warped tone
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      const baseFreq = 150 + Math.random() * 100;
      osc.frequency.setValueAtTime(baseFreq, t);
      osc.frequency.linearRampToValueAtTime(baseFreq * 2.5, t + dur * 0.2);
      osc.frequency.linearRampToValueAtTime(baseFreq * 0.3, t + dur);

      // Heavy bandpass for alien quality
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 400 + Math.random() * 300;
      filter.Q.value = 8;

      // Secondary high-pitched static
      const noise = this._noiseBuffer(dur * 0.5);
      const noiseSrc = ctx.createBufferSource();
      noiseSrc.buffer = noise;
      const noiseFilter = ctx.createBiquadFilter();
      noiseFilter.type = 'highpass';
      noiseFilter.frequency.value = 3000;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.08, t);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, t + dur * 0.5);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.01, t);
      gain.gain.linearRampToValueAtTime(0.35, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.01, t + dur);

      osc.connect(filter).connect(gain).connect(output);
      noiseSrc.connect(noiseFilter).connect(noiseGain).connect(output);
      osc.start(t);
      osc.stop(t + dur);
      noiseSrc.start(t);
    });
  }

  // --- SLIME: wet squelchy bounce sound ---
  playSlimeSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 20, (ctx, output) => {
      const t = ctx.currentTime;
      const dur = 0.2 + Math.random() * 0.1;

      // Low wet thud
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      const baseFreq = 100 + Math.random() * 50;
      osc.frequency.setValueAtTime(baseFreq * 1.5, t);
      osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.5, t + dur);

      // Squelch noise layer
      const noise = this._noiseBuffer(dur);
      const noiseSrc = ctx.createBufferSource();
      noiseSrc.buffer = noise;
      const noiseFilter = ctx.createBiquadFilter();
      noiseFilter.type = 'bandpass';
      noiseFilter.frequency.setValueAtTime(800, t);
      noiseFilter.frequency.exponentialRampToValueAtTime(200, t + dur);
      noiseFilter.Q.value = 3;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.2, t);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, t + dur);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.4, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + dur);

      osc.connect(gain).connect(output);
      noiseSrc.connect(noiseFilter).connect(noiseGain).connect(output);
      osc.start(t);
      osc.stop(t + dur);
      noiseSrc.start(t);

      // Optional second squelch
      if (Math.random() < 0.5) {
        const offset = dur + 0.05;
        const osc2 = ctx.createOscillator();
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(baseFreq * 1.2, t + offset);
        osc2.frequency.exponentialRampToValueAtTime(baseFreq * 0.4, t + offset + dur * 0.8);
        const g2 = ctx.createGain();
        g2.gain.setValueAtTime(0.25, t + offset);
        g2.gain.exponentialRampToValueAtTime(0.01, t + offset + dur * 0.8);
        osc2.connect(g2).connect(output);
        osc2.start(t + offset);
        osc2.stop(t + offset + dur * 0.8);
      }
    });
  }

  // --- WOLF: bark / growl ---
  playWolfSound(playerPos, mobPos) {
    this._playMobSound(playerPos, mobPos, 22, (ctx, output) => {
      const t = ctx.currentTime;
      const isGrowl = Math.random() < 0.4;

      if (isGrowl) {
        // Low growl
        const dur = 0.4 + Math.random() * 0.2;
        const osc = ctx.createOscillator();
        osc.type = 'sawtooth';
        const baseFreq = 80 + Math.random() * 30;
        osc.frequency.setValueAtTime(baseFreq, t);
        osc.frequency.linearRampToValueAtTime(baseFreq * 0.8, t + dur);

        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 300;
        filter.Q.value = 3;

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.01, t);
        gain.gain.linearRampToValueAtTime(0.35, t + 0.05);
        gain.gain.setValueAtTime(0.3, t + dur * 0.7);
        gain.gain.exponentialRampToValueAtTime(0.01, t + dur);

        osc.connect(filter).connect(gain).connect(output);
        osc.start(t);
        osc.stop(t + dur);
      } else {
        // Short bark (1-2 barks)
        const numBarks = 1 + Math.floor(Math.random() * 2);
        for (let i = 0; i < numBarks; i++) {
          const offset = i * 0.18;
          const barkDur = 0.1;
          const osc = ctx.createOscillator();
          osc.type = 'sawtooth';
          const freq = 250 + Math.random() * 100;
          osc.frequency.setValueAtTime(freq * 1.3, t + offset);
          osc.frequency.exponentialRampToValueAtTime(freq * 0.6, t + offset + barkDur);

          const filter = ctx.createBiquadFilter();
          filter.type = 'bandpass';
          filter.frequency.value = 600;
          filter.Q.value = 3;

          const gain = ctx.createGain();
          gain.gain.setValueAtTime(0.01, t + offset);
          gain.gain.linearRampToValueAtTime(0.5, t + offset + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.01, t + offset + barkDur);

          osc.connect(filter).connect(gain).connect(output);
          osc.start(t + offset);
          osc.stop(t + offset + barkDur + 0.02);
        }
      }
    });
  }

  // --- Player damage sound ---
  playDamage() {
    this._init();
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(300, t);
    osc.frequency.exponentialRampToValueAtTime(100, t + 0.15);
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.2, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.15);
    osc.connect(gain).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + 0.15);
  }

  // --- Sword swing whoosh ---
  playSwing() {
    this._init();
    const t = this.ctx.currentTime;
    const dur = 0.12;
    const buf = this._noiseBuffer(dur);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(2000, t);
    filter.frequency.exponentialRampToValueAtTime(800, t + dur);
    filter.Q.value = 1.5;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(filter).connect(gain).connect(this.masterSend);
    src.start(t);
  }

  // --- Melee hit impact (thud + clink) ---
  playHitImpact() {
    this._init();
    const t = this.ctx.currentTime;
    // Thud: low sine burst
    const thud = this.ctx.createOscillator();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(150, t);
    thud.frequency.exponentialRampToValueAtTime(60, t + 0.08);
    const thudGain = this.ctx.createGain();
    thudGain.gain.setValueAtTime(0.25, t);
    thudGain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    thud.connect(thudGain).connect(this.masterSend);
    thud.start(t);
    thud.stop(t + 0.1);
    // Clink: high noise burst
    const clinkDur = 0.06;
    const clinkBuf = this._noiseBuffer(clinkDur);
    const clink = this.ctx.createBufferSource();
    clink.buffer = clinkBuf;
    const clinkFilter = this.ctx.createBiquadFilter();
    clinkFilter.type = 'highpass';
    clinkFilter.frequency.value = 3000;
    const clinkGain = this.ctx.createGain();
    clinkGain.gain.setValueAtTime(0.1, t);
    clinkGain.gain.exponentialRampToValueAtTime(0.001, t + clinkDur);
    clink.connect(clinkFilter).connect(clinkGain).connect(this.masterSend);
    clink.start(t);
  }

  // --- Explosion sound ---
  playExplosion() {
    this._init();
    const t = this.ctx.currentTime;
    const dur = 0.6;
    const buf = this._noiseBuffer(dur);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, t);
    filter.frequency.exponentialRampToValueAtTime(100, t + dur);
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.6, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
    src.connect(filter).connect(gain).connect(this.masterSend);
    src.start(t);
    // Bass boom
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(60, t);
    osc.frequency.exponentialRampToValueAtTime(20, t + 0.4);
    const bg = this.ctx.createGain();
    bg.gain.setValueAtTime(0.5, t);
    bg.gain.exponentialRampToValueAtTime(0.01, t + 0.4);
    osc.connect(bg).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + 0.4);
  }

  // Play the appropriate sound for a given mob type
  playMobSound(type, playerPos, mobPos) {
    switch (type) {
      case 'PIG': this.playPigSound(playerPos, mobPos); break;
      case 'SHEEP': this.playSheepSound(playerPos, mobPos); break;
      case 'COW': this.playCowSound(playerPos, mobPos); break;
      case 'CHICKEN': this.playChickenSound(playerPos, mobPos); break;
      case 'VILLAGER': this.playVillagerSound(playerPos, mobPos); break;
      case 'ZOMBIE': this.playZombieSound(playerPos, mobPos); break;
      case 'SKELETON': this.playSkeletonSound(playerPos, mobPos); break;
      case 'CREEPER': this.playCreeperSound(playerPos, mobPos); break;
      case 'SPIDER': this.playSpiderSound(playerPos, mobPos); break;
      case 'ENDERMAN': this.playEndermanSound(playerPos, mobPos); break;
      case 'SLIME': this.playSlimeSound(playerPos, mobPos); break;
      case 'WOLF': this.playWolfSound(playerPos, mobPos); break;
    }
  }

  playLevelUp() {
    this._init();
    const t = this.ctx.currentTime;
    // Rising arpeggio (like Minecraft level up)
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = this.ctx.createGain();
      const start = t + i * 0.08;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.15, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.3);
      osc.connect(gain).connect(this.masterSend);
      osc.start(start);
      osc.stop(start + 0.35);
    });
  }

  playEnchant() {
    this._init();
    const t = this.ctx.currentTime;
    // Mystical shimmer sound
    for (let i = 0; i < 3; i++) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      const baseFreq = 800 + i * 200;
      osc.frequency.setValueAtTime(baseFreq, t);
      osc.frequency.exponentialRampToValueAtTime(baseFreq * 1.5, t + 0.4);
      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.08, t + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
      osc.connect(gain).connect(this.masterSend);
      osc.start(t);
      osc.stop(t + 0.5);
    }
  }

  playEat() {
    this._init();
    const t = this.ctx.currentTime;
    // Crunchy eating sound
    for (let i = 0; i < 3; i++) {
      const buf = this._noiseBuffer(0.08);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const gain = this.ctx.createGain();
      const start = t + i * 0.12;
      gain.gain.setValueAtTime(0.15, start);
      gain.gain.exponentialRampToValueAtTime(0.01, start + 0.06);
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 1200 + Math.random() * 600;
      filter.Q.value = 2;
      src.connect(filter).connect(gain).connect(this.masterSend);
      src.start(start);
    }
  }

  playThunder() {
    this._init();
    const t = this.ctx.currentTime;
    // Deep rumbling thunder
    const buf = this._noiseBuffer(1.5);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.4, t + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.15, t + 0.3);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 1.5);
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 200;
    filter.Q.value = 0.5;
    src.connect(filter).connect(gain).connect(this.masterSend);
    src.start(t);
    // Sharp crack
    const crack = this.ctx.createBufferSource();
    crack.buffer = this._noiseBuffer(0.1);
    const crackGain = this.ctx.createGain();
    crackGain.gain.setValueAtTime(0.3, t);
    crackGain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    const crackFilter = this.ctx.createBiquadFilter();
    crackFilter.type = 'highpass';
    crackFilter.frequency.value = 2000;
    crack.connect(crackFilter).connect(crackGain).connect(this.masterSend);
    crack.start(t);
  }

  playXPPickup() {
    this._init();
    const t = this.ctx.currentTime;
    // High-pitched ping (like Minecraft XP orb)
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1400 + Math.random() * 400, t);
    osc.frequency.exponentialRampToValueAtTime(800, t + 0.15);
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    osc.connect(gain).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + 0.25);
  }

  playLevelUp() {
    this._init();
    const t = this.ctx.currentTime;
    // Ascending arpeggio: C5 → E5 → G5
    const notes = [523.25, 659.25, 783.99];
    notes.forEach((freq, i) => {
      const osc = this.ctx.createOscillator();
      osc.type = 'triangle';
      const startT = t + i * 0.12;
      osc.frequency.setValueAtTime(freq, startT);
      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.18, startT);
      gain.gain.exponentialRampToValueAtTime(0.001, startT + 0.4);
      osc.connect(gain).connect(this.masterSend);
      osc.start(startT);
      osc.stop(startT + 0.45);
    });
  }

  setVolume(value) {
    // value should be 0.0 to 1.0
    this._init();
    this.masterGain.gain.value = 0.3 * value;
  }

  // ==================== AMBIENT SOUNDSCAPE ====================

  /**
   * Update ambient sounds based on environment.
   * Call once per tick from game loop.
   * @param {number} dt
   * @param {{ isDay: boolean, isUnderground: boolean, playerY: number, biome: string, nearLava: boolean }} env
   */
  updateAmbience(dt, env) {
    this._init();
    if (!this._ambientTimers) {
      this._ambientTimers = { bird: 0, cricket: 0, wind: 0, drip: 0, lava: 0 };
    }
    const t = this._ambientTimers;

    // Birds during daytime (surface only)
    if (env.isDay && !env.isUnderground) {
      t.bird -= dt;
      if (t.bird <= 0) {
        t.bird = 3 + Math.random() * 5; // 3-8s interval
        this._playBirdChirp();
      }
    }

    // Crickets at night (surface only)
    if (!env.isDay && !env.isUnderground) {
      t.cricket -= dt;
      if (t.cricket <= 0) {
        t.cricket = 0.8 + Math.random() * 2; // 0.8-2.8s interval
        this._playCricket();
      }
    }

    // Wind on mountains (Y > 80)
    if (env.playerY > 80 && !env.isUnderground) {
      t.wind -= dt;
      if (t.wind <= 0) {
        t.wind = 4 + Math.random() * 6; // 4-10s interval
        this._playWind();
      }
    }

    // Drip in caves
    if (env.isUnderground) {
      t.drip -= dt;
      if (t.drip <= 0) {
        t.drip = 1.5 + Math.random() * 4; // 1.5-5.5s interval
        this._playDrip();
      }
    }

    // Biome-specific ambience (Fase 11)
    if (!this._ambientTimers.biome) this._ambientTimers.biome = 0;
    if (env.biome && !env.isUnderground) {
      t.biome -= dt;
      if (t.biome <= 0) {
        t.biome = 2 + Math.random() * 4;
        if (env.biome === 'jungle' || env.biome === 5) this._playJungleInsects();
        else if (env.biome === 'swamp' || env.biome === 6) this._playSwampFrogs();
        else if (env.biome === 'desert' || env.biome === 1) this._playDesertWind();
        else if (env.biome === 'tundra' || env.biome === 3) this._playIceCreak();
      }
    }

    // Lava bubbling
    if (env.nearLava) {
      t.lava -= dt;
      if (t.lava <= 0) {
        t.lava = 0.5 + Math.random() * 1.5; // 0.5-2s interval
        this._playLavaBubble();
      }
    }
  }

  _playBirdChirp() {
    const t = this.ctx.currentTime;
    // 2-3 note chirp (ascending sine tones)
    const noteCount = 2 + Math.floor(Math.random() * 2);
    const baseFreq = 2000 + Math.random() * 1500; // 2000-3500Hz
    for (let i = 0; i < noteCount; i++) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      const freq = baseFreq + i * (200 + Math.random() * 300);
      osc.frequency.setValueAtTime(freq, t + i * 0.1);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.1, t + i * 0.1 + 0.05);
      const g = this.ctx.createGain();
      const start = t + i * 0.1;
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(0.04, start + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.08);
      // Random panning for spatial feel
      const pan = this.ctx.createStereoPanner();
      pan.pan.value = (Math.random() - 0.5) * 1.6;
      osc.connect(g).connect(pan).connect(this.masterSend);
      osc.start(start);
      osc.stop(start + 0.1);
    }
  }

  _playCricket() {
    const t = this.ctx.currentTime;
    // High-frequency buzz (2-4 rapid chirps)
    const chirps = 2 + Math.floor(Math.random() * 3);
    for (let i = 0; i < chirps; i++) {
      const osc = this.ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = 4000 + Math.random() * 1000;
      const g = this.ctx.createGain();
      const start = t + i * 0.06;
      g.gain.setValueAtTime(0.015, start);
      g.gain.setValueAtTime(0.001, start + 0.03);
      const pan = this.ctx.createStereoPanner();
      pan.pan.value = (Math.random() - 0.5) * 1.8;
      osc.connect(g).connect(pan).connect(this.masterSend);
      osc.start(start);
      osc.stop(start + 0.04);
    }
  }

  _playWind() {
    const t = this.ctx.currentTime;
    const dur = 2 + Math.random() * 2; // 2-4s gust
    const buf = this._noiseBuffer(dur);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 400 + Math.random() * 200;
    filter.Q.value = 0.5;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.06, t + dur * 0.3);
    g.gain.linearRampToValueAtTime(0.04, t + dur * 0.7);
    g.gain.linearRampToValueAtTime(0, t + dur);
    src.connect(filter).connect(g).connect(this.masterSend);
    src.start(t);
  }

  _playDrip() {
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    const freq = 1500 + Math.random() * 1000;
    osc.frequency.setValueAtTime(freq, t);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.5, t + 0.08);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.06, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    const pan = this.ctx.createStereoPanner();
    pan.pan.value = (Math.random() - 0.5) * 1.4;
    osc.connect(g).connect(pan).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + 0.15);
  }

  _playLavaBubble() {
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(80 + Math.random() * 40, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.15);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.08, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    // Noise crackle on top
    const noiseBuf = this._noiseBuffer(0.1);
    const noise = this.ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const nf = this.ctx.createBiquadFilter();
    nf.type = 'lowpass';
    nf.frequency.value = 300;
    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0.03, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.connect(g).connect(this.masterSend);
    noise.connect(nf).connect(ng).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + 0.25);
    noise.start(t);
  }

  // ==================== BIOME-SPECIFIC SOUNDS (Fase 11) ====================

  _playJungleInsects() {
    const t = this.ctx.currentTime;
    // Cicada-like buzzing
    for (let i = 0; i < 3; i++) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = 3000 + Math.random() * 2000;
      const g = this.ctx.createGain();
      const start = t + i * 0.15;
      g.gain.setValueAtTime(0.01, start);
      g.gain.setValueAtTime(0.02, start + 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.12);
      const pan = this.ctx.createStereoPanner();
      pan.pan.value = (Math.random() - 0.5) * 1.6;
      osc.connect(g).connect(pan).connect(this.masterSend);
      osc.start(start);
      osc.stop(start + 0.15);
    }
  }

  _playSwampFrogs() {
    const t = this.ctx.currentTime;
    // Deep frog croak
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150 + Math.random() * 100, t);
    osc.frequency.exponentialRampToValueAtTime(80, t + 0.3);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.06, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    const pan = this.ctx.createStereoPanner();
    pan.pan.value = (Math.random() - 0.5) * 1.4;
    osc.connect(g).connect(pan).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + 0.5);
  }

  _playDesertWind() {
    const t = this.ctx.currentTime;
    const dur = 1.5 + Math.random() * 1.5;
    const buf = this._noiseBuffer(dur);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 800 + Math.random() * 400;
    filter.Q.value = 0.3;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.03, t + dur * 0.2);
    g.gain.linearRampToValueAtTime(0.02, t + dur * 0.8);
    g.gain.linearRampToValueAtTime(0, t + dur);
    src.connect(filter).connect(g).connect(this.masterSend);
    src.start(t);
  }

  _playIceCreak() {
    const t = this.ctx.currentTime;
    // Ice cracking sound
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800 + Math.random() * 600, t);
    osc.frequency.exponentialRampToValueAtTime(200, t + 0.15);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.04, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    osc.connect(g).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + 0.25);
  }

  // ============================================================
  //  UI SOUND EFFECTS
  // ============================================================

  /** Soft cloth/bag opening sound */
  playInventoryOpen() {
    this._init();
    const t = this.ctx.currentTime;
    // Soft whoosh + cloth rustle
    const dur = 0.15;
    const buf = this._noiseBuffer(dur);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(600, t);
    filter.frequency.linearRampToValueAtTime(1200, t + dur);
    filter.Q.value = 1.2;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.01, t);
    gain.gain.linearRampToValueAtTime(0.1, t + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
    src.connect(filter).connect(gain).connect(this.masterSend);
    src.start(t);
    // Subtle low tone
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(200, t);
    osc.frequency.linearRampToValueAtTime(300, t + 0.08);
    const og = this.ctx.createGain();
    og.gain.setValueAtTime(0.06, t);
    og.gain.exponentialRampToValueAtTime(0.01, t + 0.1);
    osc.connect(og).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + 0.1);
  }

  /** Soft cloth closing sound */
  playInventoryClose() {
    this._init();
    const t = this.ctx.currentTime;
    // Reverse whoosh (descending)
    const dur = 0.12;
    const buf = this._noiseBuffer(dur);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(1200, t);
    filter.frequency.linearRampToValueAtTime(400, t + dur);
    filter.Q.value = 1.0;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.08, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
    src.connect(filter).connect(gain).connect(this.masterSend);
    src.start(t);
    // Subtle low thud
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(250, t);
    osc.frequency.exponentialRampToValueAtTime(120, t + 0.08);
    const og = this.ctx.createGain();
    og.gain.setValueAtTime(0.05, t);
    og.gain.exponentialRampToValueAtTime(0.01, t + 0.1);
    osc.connect(og).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + 0.1);
  }

  /** Satisfying "ding" for successful craft — ascending 2-note */
  playCraftSuccess() {
    this._init();
    const t = this.ctx.currentTime;
    // Note 1: E5
    const osc1 = this.ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 659.25;
    const g1 = this.ctx.createGain();
    g1.gain.setValueAtTime(0.15, t);
    g1.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
    osc1.connect(g1).connect(this.masterSend);
    osc1.start(t);
    osc1.stop(t + 0.3);
    // Note 2: A5 (higher, delayed)
    const osc2 = this.ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 880;
    const g2 = this.ctx.createGain();
    g2.gain.setValueAtTime(0.001, t + 0.08);
    g2.gain.linearRampToValueAtTime(0.15, t + 0.1);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    osc2.connect(g2).connect(this.masterSend);
    osc2.start(t + 0.08);
    osc2.stop(t + 0.4);
  }

  /** Triumphant fanfare for achievement unlock — 3-note ascending chord */
  playAchievementUnlock() {
    this._init();
    const t = this.ctx.currentTime;
    // C5 -> E5 -> G5, with overlapping sustain for a chord feel
    const notes = [
      { freq: 523.25, delay: 0, type: 'triangle' },
      { freq: 659.25, delay: 0.1, type: 'triangle' },
      { freq: 783.99, delay: 0.2, type: 'sine' },
    ];
    notes.forEach(({ freq, delay, type }) => {
      const osc = this.ctx.createOscillator();
      osc.type = type;
      osc.frequency.value = freq;
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.001, t + delay);
      g.gain.linearRampToValueAtTime(0.14, t + delay + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, t + delay + 0.6);
      osc.connect(g).connect(this.masterSend);
      osc.start(t + delay);
      osc.stop(t + delay + 0.65);
    });
    // Shimmer noise on top
    const buf = this._noiseBuffer(0.3);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 6000;
    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0.01, t + 0.2);
    ng.gain.linearRampToValueAtTime(0.04, t + 0.3);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    src.connect(filter).connect(ng).connect(this.masterSend);
    src.start(t + 0.2);
  }

  /** Subtle UI click for buttons */
  playButtonClick() {
    this._init();
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1000, t);
    osc.frequency.exponentialRampToValueAtTime(600, t + 0.03);
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.08, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    osc.connect(gain).connect(this.masterSend);
    osc.start(t);
    osc.stop(t + 0.06);
  }

  _noiseBuffer(duration) {
    const sr = this.ctx.sampleRate;
    const len = Math.floor(sr * duration);
    const buf = this.ctx.createBuffer(1, len, sr);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    return buf;
  }
}
